import React, { useState, useEffect, useCallback } from "react";
import { Dropdown } from "primereact/dropdown";
import { FilterMatchMode } from "primereact/api";
import * as masterSelectors from "../../../../Store/Dashboard/AshaAnmSection/AshaAnmSectionSelectors";
import { fetchTopAshaData } from "../../../../Store/Dashboard/AshaAnmSection/AshaAnmSectionActions";
import { useSelector, useDispatch } from "react-redux";
import SendReminderView from "../../../../Shared/Component/SendReminder/SendReminderView";
import "./AshaDetailView.scss";
import { getGlobalAreaType } from "../../../../Store/Dashboard/DashboardSelectors";
import PropTypes from "prop-types";
import {
  ashaDetailFilterTemplate,
  createDropdownFilterTemplate,
  getColumns,
  reminderBodyTemplate,
} from "../../../../Shared/Utils/tableColumns";
import NoRecordFound from "../../../../Shared/Component/NoRecordFound/NoRecordFound";
import { useLanguage } from "../../../ChangeLanguage/LanguageProvider";
import CustomDataTable from "../../../../Shared/Component/CustomTable/CustomDataTable";
import GetAreaId from "../../../../Shared/Utils/getAreaId";
import { useTranslation } from "react-i18next";

const AshaDetailsTable = ({ handleAshaClick }) => {
  const { t } = useTranslation();
  const { selectedLanguageId } = useLanguage();
  const dispatch = useDispatch();
  const sort = ["highestToLowest", "lowestToHighest"];
  const roles = ["Asha", "ANM"];
  const isAdmin = sessionStorage.getItem("userRole");
  const columns = getColumns(isAdmin, "AshaDetails", true, t);
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const year = [
    "2013",
    "2014",
    "2015",
    "2016",
    "2017",
    "2018",
    "2019",
    "2020",
    "2021",
    "2022",
    "2023",
    "2024",
    "2025",
  ];
  const [data, setData] = useState([]);
  const topPerformerData = useSelector(masterSelectors.getTopPerformerData);
  const areaTypeName = useSelector(getGlobalAreaType);
  const [selectedItems, setSelectedItems] = useState([]);
  const [totalActivityCount, setTotalActivityCount] = useState([]);
  const [, setFilters] = useState(null);
  const currentMonth = new Date().getMonth();
  const [selectedMonth, setSelectedMonth] = useState(months[currentMonth]);
  const [selectedYear, setSelectedYear] = useState(year[11]);
  const [selectedRoles, setSelectedRoles] = useState(roles[0]);
  const [selectedSort, setSelectedSort] = useState(sort[0]);
  const [districtName, setDistrictName] = useState([]);
  const [activeCases, setActiveCases] = useState([]);
  const [actualIncentive, setActualIncentive] = useState([]);
  const [paidIncentive, setPaidIncentive] = useState([]);

  const initFilters = () => {
    setFilters({
      ashaid: { value: "", matchMode: FilterMatchMode.CONTAINS },
      ashaName: { value: "", matchMode: FilterMatchMode.CONTAINS },
      contactNo: { value: "", matchMode: FilterMatchMode.CONTAINS },
      district: { value: null, matchMode: FilterMatchMode.EQUALS },
      talukas: { value: null, matchMode: FilterMatchMode.EQUALS },
      subcenterName: { value: null, matchMode: FilterMatchMode.EQUALS },
      phcName: { value: null, matchMode: FilterMatchMode.EQUALS },
      activeCases: { value: null, matchMode: FilterMatchMode.EQUALS },
      actualIncentive: { value: null, matchMode: FilterMatchMode.EQUALS },
      paidIncentive: { value: null, matchMode: FilterMatchMode.EQUALS },
      totalActivityCount: { value: null, matchMode: FilterMatchMode.EQUALS },
      reminderCount: { value: "", matchMode: FilterMatchMode.CONTAINS },
    });
  };

  function checkAdmin(role) {
    const adminRoles = ["Admin", "Support Admin"];
    return adminRoles.includes(role);
  }

  const result = checkAdmin(isAdmin);
  const isValidTopPerformerData = useCallback((data) => {
    return Array.isArray(data) && data.length > 0;
  }, []);

  const resetStateToInitial = useCallback((setStateFunctions) => {
    Object.values(setStateFunctions).forEach((setFunc) => setFunc([]));
  }, []);

  const setComponentStateData = useCallback(
    (topPerformerData, setStateFunctions) => {
      const initialState = {
        district: [],
      };

      if (!isValidTopPerformerData(topPerformerData)) {
        resetStateToInitial(setStateFunctions);
        return;
      }

      const elementToStateMapping = {
        district: "district",
      };

      const updatedState = topPerformerData.reduce((acc, el) => {
        Object.entries(elementToStateMapping).forEach(([key, value]) => {
          if (el[key] !== undefined) {
            acc[value].push(el[key]);
          }
        });
        return acc;
      }, initialState);

      const { setDistrictName } = setStateFunctions;
      setDistrictName(updatedState.district);
    },
    [isValidTopPerformerData, resetStateToInitial]
  );

  const districtFilterTemplate = createDropdownFilterTemplate(districtName, t);
  const ActiveCaseFilterTemplate = createDropdownFilterTemplate(activeCases, t);
  const actualIncentiveFilterTemplate = createDropdownFilterTemplate(
    actualIncentive,
    t
  );
  const paidIncentiveFilterTemplate = createDropdownFilterTemplate(
    paidIncentive,
    t
  );
  const activitiesFilterTemplate = createDropdownFilterTemplate(
    totalActivityCount,
    t
  );

  const handleSort = (val) => {
    setSelectedSort(val);
    const sortType = val === sort[0] ? "highestToLowest" : "lowestToHighest";
    dispatch(
      fetchTopAshaData(
        GetAreaId(areaTypeName),
        selectedMonth,
        selectedYear,
        selectedLanguageId,
        sortType
      )
    );
  };

  const handleMonth = (val) => {
    setSelectedMonth(val);
    dispatch(
      fetchTopAshaData(
        GetAreaId(areaTypeName),
        val,
        selectedYear,
        selectedLanguageId,
        selectedSort
      )
    );
  };
  const handleYear = (val) => {
    setSelectedYear(val);
    dispatch(
      fetchTopAshaData(
        GetAreaId(areaTypeName),
        selectedMonth,
        val,
        selectedLanguageId,
        selectedSort
      )
    );
  };

  useEffect(() => {
    setData(topPerformerData);

    if (topPerformerData) {
      setComponentStateData(topPerformerData, {
        setTotalActivityCount,
        setActualIncentive,
        setPaidIncentive,
        setActiveCases,
        setDistrictName,
      });
    }
  }, [topPerformerData, setComponentStateData]);

  useEffect(() => {
    initFilters();
  }, []);

  const SelectedIds = (items) => {
    let idsList = [];
    items.length > 0 &&
      items.forEach((el) => {
        idsList.push(el.ashaId);
      });
    return idsList;
  };

  const handleAshaName = (rowData) => (
    <div
      className="asha-selection text-left"
      onClick={() => handleAshaClick(rowData.ashaid, rowData.ashaName, 0)}
      data-testid={`asha-name-${rowData.ashaId}`}
    >
      {rowData.ashaName}
    </div>
  );

  const handleActivity = (rowData) => (
    <div
      className="asha-selection text-left"
      data-testid={`activity-count-${rowData.ashaId}`}
    >
      {rowData.totalActivityCount}
    </div>
  );

  const subcenterNameTemplate = (props) => {
    return (
      <div className="text-center">
        {props.subcenterName.map((item) => (
          <div key={item}>{item},</div>
        ))}
      </div>
    );
  };

  const bodyMethods = {
    ashaName: handleAshaName,
    totalActivityCount: handleActivity,
    RemainderCount: reminderBodyTemplate,
    subcenterName: subcenterNameTemplate,
  };

  const filterMethods = {
    ashaName: (options) => ashaDetailFilterTemplate(options, t),
    ashaid: (options) => ashaDetailFilterTemplate(options, t),
    mobileNo: (options) => ashaDetailFilterTemplate(options, t),
    subcenterName: (options) => ashaDetailFilterTemplate(options, t),
    stateName: (options) => ashaDetailFilterTemplate(options, t),
    totalActiveCases: (options) => ashaDetailFilterTemplate(options, t),
    amount: (options) => ashaDetailFilterTemplate(options, t),
    villagename: (options) => ashaDetailFilterTemplate(options, t),
    talukas: (options) => ashaDetailFilterTemplate(options, t),
    phcName: (options) => ashaDetailFilterTemplate(options, t),
    district: districtFilterTemplate,
    activeCases: ActiveCaseFilterTemplate,
    actualIncentive: actualIncentiveFilterTemplate,
    paidIncentive: paidIncentiveFilterTemplate,
    totalActivityCount: activitiesFilterTemplate,
    RemainderCount: (options) => ashaDetailFilterTemplate(options, t),
  };
  const className = result ? "admintabdesign" : "tabdesign";
  return (
    <div>
      <div className="flex flex-wrap  justify-content-between gap-8">
        <div className="flex flex-wrap align-items-center mt-3 gap-2  ">
          <div className="flex text-xl ml-4 text-blue-800 font-light">
            {t("top5ashatitle")}
          </div>
          <div className="flex align-items-center justify-content-center mt-1">
            <Dropdown
              value={selectedSort}
              onChange={(e) => handleSort(e.value)}
              options={sort}
              className="cockpit-dropdown"
              data-testid="sort-dropdown"
            />
          </div>

          <div className="flex align-items-center justify-content-center mt-1">
            <Dropdown
              value={selectedRoles}
              onChange={(e) => setSelectedRoles(e.value)}
              options={roles}
              className="cockpit-dropdown"
              disabled
              data-testid="roles-dropdown"
            />
          </div>

          <div className="flex align-items-center justify-content-center mt-1">
            <Dropdown
              data-testid="custom-dropdown"
              value={selectedMonth}
              onChange={(e) => handleMonth(e.value)}
              options={months}
              className="cockpit-dropdown"
            />
          </div>

          <div className="flex align-items-center justify-content-center mt-1">
            <Dropdown
              value={selectedYear}
              onChange={(e) => handleYear(e.value)}
              options={year}
              className="cockpit-dropdown"
            />
          </div>
        </div>

        <div className="mt-3 ">
          <SendReminderView
            ashaIds={SelectedIds(selectedItems)}
            isDisabled={selectedItems.length === 0}
          />
        </div>
      </div>

      <div className="mt-5">
        {Array.isArray(data) && data.length > 0 ? (
          <>
            <CustomDataTable
              className={className}
              columns={columns}
              data={data}
              dataKey={"ashaId"}
              selectedItems={selectedItems}
              onSelectionChange={(e) => setSelectedItems(e.value)}
              filterMethods={filterMethods}
              bodyMethods={bodyMethods}
            />
            <p>
              {" "}
              {data.length} {t("Totalrecord")}{" "}
            </p>
          </>
        ) : (
          <NoRecordFound data-testid="no-record-found" />
        )}
      </div>
    </div>
  );
};

AshaDetailsTable.propTypes = {
  handleAshaClick: PropTypes.func.isRequired,
};

export default AshaDetailsTable;
